import { Intervention } from '@seen/core/models/care-plans/intervention'
import { parseReferenceString } from '@seen/core/utils'
import { Button } from '@seen/ui'
import { IconX } from '@tabler/icons-react'
import type { JSX, ReactNode } from 'react'
import { InterventionDisplay } from '#src/components/care-plan/interventions/InterventionDisplay.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { ScrollAreaContent } from '#src/components/core/scroll-area/ScrollAreaContent.tsx'
import { ScrollAreaHeader } from '#src/components/core/scroll-area/ScrollAreaHeader.tsx'
import { Sheet } from '#src/components/core/sheet/Sheet.tsx'
import { SheetContent } from '#src/components/core/sheet/SheetContent.tsx'
import { IconInterventionStatus } from '#src/components/icons/IconInterventionStatus.tsx'
import { InterventionStatusCombobox } from '#src/components/InterventionStatusCombobox.tsx'
import { OwnerComboboxButton } from '#src/components/OwnerComboboxButton.tsx'

export interface InterventionSheetProps {
  intervention: Intervention | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
  headerActions?: ReactNode
}

export function InterventionSheet({
  intervention,
  open,
  onOpenChange,
  headerActions,
}: InterventionSheetProps): JSX.Element | null {
  if (!intervention) {
    return null
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" hideClose>
        <div>
          <ScrollAreaHeader className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <ResourceFieldForm
                instance={intervention}
                valueFn={(valueIntervention) => valueIntervention.status}
                patchFn={(patchIntervention, status) => {
                  patchIntervention.status = status
                }}
                render={({ field: { value, onChange, ...rest } }) => (
                  <InterventionStatusCombobox
                    onSelect={onChange}
                    selected={value}
                  >
                    <Button variant="outline" className="gap-2" {...rest}>
                      <IconInterventionStatus status={value} />
                      {Intervention.statusText(value)}
                    </Button>
                  </InterventionStatusCombobox>
                )}
              />
              <ResourceFieldForm
                instance={intervention}
                valueFn={(valueIntervention) =>
                  valueIntervention.author?.reference ?? null
                }
                patchFn={(patchIntervention, author) => {
                  patchIntervention.author =
                    author === null ? undefined : parseReferenceString(author)
                }}
                render={({ field }) => <OwnerComboboxButton {...field} />}
              />
              {headerActions}
            </div>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              <IconX size={16} />
            </Button>
          </ScrollAreaHeader>
          <ScrollAreaContent asChild>
            <div className="px-4">
              <InterventionDisplay intervention={intervention} />
            </div>
          </ScrollAreaContent>
        </div>
      </SheetContent>
    </Sheet>
  )
}
